import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { ViewConfig } from "@lib/views";
import {
    AccrualMatchColumn,
    AccrualMatchSortValue,
    MatchableItem,
    MatchItemType,
} from "./types";
import { DateCell } from "./cells/DateCell";
import { LockCell } from "./cells/LockCell";
import { ScheduleCell } from "./cells/ScheduleCell";
import { TypeCell } from "./cells/TypeCell";
import { CounterpartyCell } from "./cells/CounterpartyCell";
import { DescriptionCell } from "./cells/DescriptionCell";
import { AccountCell } from "./cells/AccountCell";
import { CategoryCell } from "./cells/CategoryCell";
import { CashLedgerCell } from "./cells/CashLedgerCell";
import { AccrualLedgerCell } from "./cells/AccrualLedgerCell";
import { ClassCell } from "./cells/ClassCell";
import { AmountCell } from "./cells/AmountCell";
import { OpenAmountCell } from "./cells/OpenAmountCell";
import { EntityCell } from "./cells/EntityCell";
import { ActionCell } from "./cells/ActionCell";
import { RemoveCell } from "./cells/RemoveCell";
import { PaymentCell } from "./cells/PaymentCell";
import { FinancialAccountCell } from "./cells/FinancialAccountCell";
import { SpacerCell } from "./cells/SpacerCell";
import sizing from "./AccrualMatchSizing.module.scss";

type AccrualMatchViewConfig = ViewConfig<
    MatchableItem,
    AccrualMatchColumn,
    AccrualMatchSortValue
>;

const ACTION_COLUMN_WIDTH = Number(sizing.actionColumnWidthPx);

type SortableColumn =
    | AccrualMatchColumn.DATE
    | AccrualMatchColumn.COUNTERPARTY
    | AccrualMatchColumn.DESCRIPTION
    | AccrualMatchColumn.ACCOUNT
    | AccrualMatchColumn.CLASS
    | AccrualMatchColumn.AMOUNT;

const SORT_VALUES_BY_COLUMN: Record<
    SortableColumn,
    { asc: AccrualMatchSortValue; desc: AccrualMatchSortValue }
> = {
    [AccrualMatchColumn.DATE]: {
        asc: AccrualMatchSortValue.DATE_ASC,
        desc: AccrualMatchSortValue.DATE_DESC,
    },
    [AccrualMatchColumn.COUNTERPARTY]: {
        asc: AccrualMatchSortValue.COUNTERPARTY_ASC,
        desc: AccrualMatchSortValue.COUNTERPARTY_DESC,
    },
    [AccrualMatchColumn.DESCRIPTION]: {
        asc: AccrualMatchSortValue.DESCRIPTION_ASC,
        desc: AccrualMatchSortValue.DESCRIPTION_DESC,
    },
    [AccrualMatchColumn.ACCOUNT]: {
        asc: AccrualMatchSortValue.ACCOUNT_ASC,
        desc: AccrualMatchSortValue.ACCOUNT_DESC,
    },
    [AccrualMatchColumn.CLASS]: {
        asc: AccrualMatchSortValue.CLASS_ASC,
        desc: AccrualMatchSortValue.CLASS_DESC,
    },
    [AccrualMatchColumn.AMOUNT]: {
        asc: AccrualMatchSortValue.AMOUNT_ASC,
        desc: AccrualMatchSortValue.AMOUNT_DESC,
    },
};

// Columns that can be sorted for each match item type, based on fields
// supported by each backend contract. `null` disables all sorting (e.g. for
// the source/selected tables in the Accrual Match drawer).
const SORTABLE_COLUMNS_BY_TYPE: Record<MatchItemType, SortableColumn[]> = {
    [MatchItemType.TRANSACTION]: [
        AccrualMatchColumn.DATE,
        AccrualMatchColumn.COUNTERPARTY,
        AccrualMatchColumn.DESCRIPTION,
        AccrualMatchColumn.ACCOUNT,
        AccrualMatchColumn.CLASS,
        AccrualMatchColumn.AMOUNT,
    ],
    [MatchItemType.INVOICE]: [
        AccrualMatchColumn.DATE,
        AccrualMatchColumn.COUNTERPARTY,
        AccrualMatchColumn.ACCOUNT,
        AccrualMatchColumn.AMOUNT,
    ],
    [MatchItemType.BILL]: [
        AccrualMatchColumn.DATE,
        AccrualMatchColumn.COUNTERPARTY,
        AccrualMatchColumn.ACCOUNT,
        AccrualMatchColumn.AMOUNT,
    ],
    [MatchItemType.LINE_ITEM]: [],
    [MatchItemType.REMAINING]: [],
};

function ledgerColumnLabels(type: MatchItemType | null): {
    cashLedgerLabel: string;
    accrualLedgerLabel: string;
} {
    if (type === MatchItemType.INVOICE) {
        return {
            cashLedgerLabel: "Income account",
            accrualLedgerLabel: "Receivables account",
        };
    }
    if (type === MatchItemType.BILL) {
        return {
            cashLedgerLabel: "Expense account",
            accrualLedgerLabel: "Payables account",
        };
    }
    return {
        cashLedgerLabel: "Cash ledger",
        accrualLedgerLabel: "Accrual ledger",
    };
}

export function createAccrualMatchViewConfig(
    type: MatchItemType | null,
    options: {
        // Required so the account-column label is always derived from the
        // source entity's basis. Defaulting it silently regressed accrual
        // entities to a "Cash ledger" label in views that omitted it.
        ledgerBasis: AccountingLedgerBasis;
        isGlFirst?: boolean;
    },
): AccrualMatchViewConfig {
    const sortableColumns = new Set<SortableColumn>(
        type ? SORTABLE_COLUMNS_BY_TYPE[type] : [],
    );

    // Cash-basis, non-GL-first workspaces classify by category rather than GL
    // account, so the account column becomes a read-only "Category" column.
    const isCategoryColumn =
        options.ledgerBasis === AccountingLedgerBasis.cash &&
        options.isGlFirst === false;

    // The GL account column reflects the source entity's accounting basis:
    // "Accrual ledger" for accrual entities, "Cash ledger" for GL-first
    // cash-basis matching.
    const accountColumnLabel = isCategoryColumn
        ? "Category"
        : `${getAccountingBasisLabel(options.ledgerBasis)} ledger`;

    const { cashLedgerLabel, accrualLedgerLabel } = ledgerColumnLabels(type);

    const sortFor = (column: SortableColumn) =>
        sortableColumns.has(column) ? SORT_VALUES_BY_COLUMN[column] : undefined;

    return {
        columns: {
            [AccrualMatchColumn.LOCK]: {
                accessorKey: "id",
                name: "Lock",
                header: "",
                cell: LockCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 32,
                maxSize: 32,
            },
            [AccrualMatchColumn.DATE]: {
                accessorKey: "date",
                name: "Date",
                header: "Date",
                cell: DateCell,
                immutable: true,
                sort: sortFor(AccrualMatchColumn.DATE),
                ...DATE_COLUMN_SIZE,
            },
            [AccrualMatchColumn.TYPE]: {
                accessorKey: "type",
                name: "Type",
                header: "Type",
                cell: TypeCell,
                minSize: 40,
                maxSize: 56,
            },
            [AccrualMatchColumn.COUNTERPARTY]: {
                accessorKey: "counterpartyName",
                name: "Counterparty",
                header: "Counterparty",
                cell: CounterpartyCell,
                sort: sortFor(AccrualMatchColumn.COUNTERPARTY),
                minSize: 120,
                maxSize: 300,
            },
            [AccrualMatchColumn.DESCRIPTION]: {
                accessorKey: "description",
                name: "Description",
                header: "Description",
                cell: DescriptionCell,
                sort: sortFor(AccrualMatchColumn.DESCRIPTION),
                minSize: 150,
                maxSize: 400,
            },
            [AccrualMatchColumn.ACCOUNT]: {
                accessorKey: isCategoryColumn ? "categoryId" : "accountIds",
                name: accountColumnLabel,
                header: accountColumnLabel,
                cell: isCategoryColumn ? CategoryCell : AccountCell,
                sort: isCategoryColumn
                    ? undefined
                    : sortFor(AccrualMatchColumn.ACCOUNT),
                minSize: 120,
                maxSize: 250,
            },
            // Standalone columns for the default match and allocation drawer
            // views (PRO-12048): Category and Cash/Accrual ledger instead of
            // the single basis-swapped `ACCOUNT` column above (still used by
            // the history audit-log view).
            [AccrualMatchColumn.CATEGORY]: {
                accessorKey: "categoryId",
                name: "Category",
                header: "Category",
                cell: CategoryCell,
                minSize: 120,
                maxSize: 250,
            },
            [AccrualMatchColumn.CASH_LEDGER]: {
                accessorKey: "cashAccountIds",
                name: cashLedgerLabel,
                header: cashLedgerLabel,
                cell: CashLedgerCell,
                minSize: 120,
                maxSize: 250,
            },
            [AccrualMatchColumn.ACCRUAL_LEDGER]: {
                accessorKey: "accrualAccountIds",
                name: accrualLedgerLabel,
                header: accrualLedgerLabel,
                cell: AccrualLedgerCell,
                minSize: 120,
                maxSize: 250,
            },
            [AccrualMatchColumn.CLASS]: {
                accessorKey: "classIds",
                name: "Class",
                header: "Class",
                cell: ClassCell,
                sort: sortFor(AccrualMatchColumn.CLASS),
                minSize: 80,
                maxSize: 200,
            },
            [AccrualMatchColumn.SCHEDULE]: {
                accessorKey: "id",
                name: "Schedule",
                header: "Schedule",
                cell: ScheduleCell,
                minSize: 160,
                maxSize: 260,
            },
            [AccrualMatchColumn.FINANCIAL_ACCOUNT]: {
                accessorKey: "financialAccountId",
                name: "Account",
                header: "Account",
                cell: FinancialAccountCell,
                minSize: 120,
                maxSize: 250,
            },
            [AccrualMatchColumn.AMOUNT]: {
                accessorKey: "amount",
                name: "Amount",
                header: "Amount",
                cell: AmountCell,
                sort: sortFor(AccrualMatchColumn.AMOUNT),
                ...AMOUNT_COLUMN_SIZE,
            },
            [AccrualMatchColumn.OPEN]: {
                accessorKey: "openAmount",
                name: "Open",
                header: "Open",
                cell: OpenAmountCell,
                ...AMOUNT_COLUMN_SIZE,
            },
            [AccrualMatchColumn.PAYMENT]: {
                accessorKey: "openAmount",
                name: "Payment",
                header: "Payment",
                cell: PaymentCell,
                immutable: true,
                hiddenFromSettings: true,
                ...AMOUNT_COLUMN_SIZE,
            },
            [AccrualMatchColumn.ENTITY]: {
                accessorKey: "entity",
                name: "Entity",
                header: "Entity",
                cell: EntityCell,
                minSize: 56,
                maxSize: 56,
            },
            [AccrualMatchColumn.ACTION]: {
                accessorKey: "id",
                name: "Action",
                header: "",
                cell: ActionCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: ACTION_COLUMN_WIDTH,
                maxSize: ACTION_COLUMN_WIDTH,
            },
            [AccrualMatchColumn.REMOVE]: {
                accessorKey: "id",
                name: "",
                header: "",
                cell: RemoveCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 40,
                maxSize: 40,
            },
            // Empty cells used by the search table to push content inward
            // (matching the matches-panel 16px inset) while keeping row
            // dividers extending edge-to-edge across the drawer. Both spacers
            // are 16px wide (matching the panel's left/right margin) so the
            // data column grid in the search table starts/ends at the same
            // x-position as the matches-panel tables.
            //
            // The matches-panel tables compensate for ViewTable's
            // `:first-child { padding-left: 24 }` and
            // `:last-child { padding-right: 24 }` rules via the
            // `flushCellEdges` className applied in AccrualMatchTable.tsx so
            // first/last data cells line up with their search-table peers.
            [AccrualMatchColumn.LEFT_SPACER]: {
                accessorKey: "id",
                name: "",
                header: "",
                cell: SpacerCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 16,
                maxSize: 16,
            },
            [AccrualMatchColumn.RIGHT_SPACER]: {
                accessorKey: "id",
                name: "",
                header: "",
                cell: SpacerCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 16,
                maxSize: 16,
            },
        },
    };
}
